import { Button, Form } from 'components';
import { useUpdateLocation } from 'api/locations/hooks';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { media } from 'utils/rwd';
import validationSchemas from './schemas';

const createEmptyAdditionalLink = () => ({ name: '', url: '' });

const getInitialAdditionalLinks = (links) => {
  if (!Array.isArray(links) || links.length === 0) return [createEmptyAdditionalLink()];

  return links.map((link) => ({
    name: link?.name || '',
    url: link?.url || '',
  }));
};

const normalizeAdditionalLinks = (links = []) =>
  links
    .map((link) => ({
      name: link?.name?.trim() || '',
      url: link?.url?.trim() || '',
    }))
    .filter(({ name, url }) => name && url);

const moveAdditionalLink = (links, index, direction) => {
  const nextLinks = [...(links || [])];
  const targetIndex = index + direction;

  if (targetIndex < 0 || targetIndex >= nextLinks.length) return nextLinks;

  [nextLinks[index], nextLinks[targetIndex]] = [nextLinks[targetIndex], nextLinks[index]];
  return nextLinks;
};

const LocationAdditionalLinksForm = ({ type = 'update', initialValues, onNext, onPrevious }) => {
  const { t } = useTranslation('ownerPortal', { keyPrefix: 'locations' });
  const { mutate: updateLocation, isPending } = useUpdateLocation();

  const formInitialValues = useMemo(
    () => ({
      uid: initialValues?.uid || undefined,
      additional_links: getInitialAdditionalLinks(initialValues?.additional_links),
    }),
    [initialValues],
  );
  const validationSchema = useMemo(() => validationSchemas.location_additional_links, []);

  const handleSubmit = useCallback(
    ({ uid, additional_links }) => {
      const payload = {
        additional_links: normalizeAdditionalLinks(additional_links),
      };

      if (type === 'update') {
        updateLocation(
          { uid, content: payload },
          {
            onSuccess: () => {
              if (onNext) onNext();
            },
          },
        );
      } else if (onNext) {
        onNext();
      }
    },
    [updateLocation, onNext, type],
  );

  return (
    <Form
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      renderDefaultButtons={false}
    >
      {({ values, setFieldValue }) => (
        <>
          <AdditionalLinksContent>
            {(values.additional_links || [createEmptyAdditionalLink()]).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <AdditionalLinkRow key={`additional-link-${index}`}>
                <AdditionalLinkField>
                  {t('onboarding.additionalLinkName')}
                  <Form.Input name={`additional_links[${index}].name`} />
                </AdditionalLinkField>
                <AdditionalLinkField>
                  {t('onboarding.additionalLinkUrl')}
                  <Form.Input name={`additional_links[${index}].url`} />
                </AdditionalLinkField>
                <AdditionalLinkActions>
                  <LinkActionButton
                    type="button"
                    onClick={() =>
                      setFieldValue(
                        'additional_links',
                        moveAdditionalLink(values.additional_links, index, -1),
                      )
                    }
                    disabled={index === 0}
                    aria-label="Move additional link up"
                  >
                    <i className="fe fe-arrow-up" style={{ fontSize: 18 }} />
                  </LinkActionButton>
                  <LinkActionButton
                    type="button"
                    onClick={() =>
                      setFieldValue(
                        'additional_links',
                        moveAdditionalLink(values.additional_links, index, 1),
                      )
                    }
                    disabled={index === (values.additional_links || []).length - 1}
                    aria-label="Move additional link down"
                  >
                    <i className="fe fe-arrow-down" style={{ fontSize: 18 }} />
                  </LinkActionButton>
                  <DeleteLinkButton
                    type="button"
                    onClick={() =>
                      setFieldValue(
                        'additional_links',
                        (values.additional_links || []).filter(
                          (__, linkIndex) => linkIndex !== index,
                        ),
                      )
                    }
                    aria-label="Delete additional link"
                  >
                    <i className="fe fe-trash" style={{ fontSize: 20 }} />
                  </DeleteLinkButton>
                </AdditionalLinkActions>
              </AdditionalLinkRow>
            ))}
            <AddLinkButton
              type="button"
              onClick={() =>
                setFieldValue('additional_links', [
                  ...(values.additional_links || []),
                  createEmptyAdditionalLink(),
                ])
              }
              aria-label="Add additional link"
            >
              <i className="fe fe-plus" style={{ fontSize: 20 }} />
            </AddLinkButton>
          </AdditionalLinksContent>

          <ButtonsSection>
            {onPrevious && (
              <Button variant="secondary" block onClick={onPrevious}>
                {t('onboarding.previousStep')}
              </Button>
            )}
            <Form.Submit
              className="text-uppercase mt-0"
              size="lg"
              variant="primary"
              block
              isLoading={isPending}
              ignoreClickedState
            >
              {t('onboarding.nextStep')}
            </Form.Submit>
          </ButtonsSection>
        </>
      )}
    </Form>
  );
};

const AdditionalLinksContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AdditionalLinkRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: start;

  ${media.sm`
    grid-template-columns: 1fr 1fr auto;
  `}
`;

const AdditionalLinkField = styled.label`
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  width: 100%;
`;

const AddLinkButton = styled.button`
  width: 100%;
  min-height: 44px;
  border: 1px dashed #ced4da;
  border-radius: 4px;
  background: #fff;
  color: #495057;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f8f9fa;
  }
`;

const AdditionalLinkActions = styled.div`
  align-self: start;
  margin-top: 32px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const LinkActionButton = styled.button`
  width: 42px;
  height: 44px;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: none;
  transition: color 0.2s;

  i {
    color: #495057;
  }

  &:hover:not(:disabled) i {
    color: #000;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.35;
  }
`;

const DeleteLinkButton = styled(LinkActionButton)`
  i {
    color: #be1622;
  }

  &:hover:not(:disabled) i {
    color: #a2131d;
  }
`;

const ButtonsSection = styled.div`
  width: 100%;
  margin-top: 48px;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
`;

LocationAdditionalLinksForm.propTypes = {
  type: PropTypes.oneOf(['update']),
  initialValues: PropTypes.shape({
    uid: PropTypes.string,
    additional_links: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        url: PropTypes.string,
      }),
    ),
  }),
  onNext: PropTypes.func,
  onPrevious: PropTypes.func,
};

export default LocationAdditionalLinksForm;
